import { useState, useEffect, useCallback } from 'react';
import { Input, Menu, Tabs, Empty, Badge, Spin, message } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AppstoreOutlined, StarOutlined, EditOutlined, RocketOutlined,
  BarChartOutlined, TeamOutlined, ThunderboltOutlined,
  SearchOutlined, PieChartOutlined, DollarOutlined, CheckCircleOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons';
import PluginCard from '../../components/PluginCard';
import { getPlugins, install, uninstall, getCategoryCounts } from '../../api/marketplace';

const { Search } = Input;

// —— 分类图标映射 ——
const categoryIconMap = {
  all: <AppstoreOutlined />,
  featured: <StarOutlined />,
  content: <EditOutlined />,
  advertising: <RocketOutlined />,
  analytics: <BarChartOutlined />,
  influencer: <TeamOutlined />,
  efficiency: <ThunderboltOutlined />,
};

// —— 分类中文 ——
const categoryLabels = {
  all: '全部',
  featured: '官方推荐',
  content: '内容创作',
  advertising: '广告投放',
  analytics: '数据分析',
  influencer: '达人营销',
  efficiency: '效率工具',
};

const Marketplace = () => {
  const [plugins, setPlugins] = useState([]);
  const [installedIds, setInstalledIds] = useState(new Set(['p002', 'p012']));
  const [categoryCounts, setCategoryCounts] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);

  // 加载分类计数
  useEffect(() => {
    getCategoryCounts().then(setCategoryCounts).catch(() => {});
  }, []);

  // 加载插件列表
  const loadPlugins = useCallback(async (category, keyword) => {
    setLoading(true);
    try {
      let result = await getPlugins(category === 'all' ? undefined : category);
      // 前端搜索过滤
      if (keyword) {
        const kw = keyword.toLowerCase();
        result = result.filter(
          (p) =>
            p.name.toLowerCase().includes(kw) ||
            p.description.toLowerCase().includes(kw) ||
            p.tags.some((t) => t.toLowerCase().includes(kw))
        );
      }
      // Tab 过滤
      if (activeTab === 'installed') {
        result = result.filter((p) => installedIds.has(p.id));
      } else if (activeTab === 'paid') {
        result = result.filter((p) => p.priceType === 'paid');
      } else if (activeTab === 'free') {
        result = result.filter((p) => p.priceType === 'free');
      }
      setPlugins(result);
    } finally {
      setLoading(false);
    }
  }, [activeTab, installedIds]);

  useEffect(() => {
    loadPlugins(activeCategory, searchKeyword);
  }, [activeCategory, searchKeyword, loadPlugins]);

  // 安装
  const handleInstall = async (pluginId) => {
    try {
      await install(pluginId);
      setInstalledIds((prev) => new Set([...prev, pluginId]));
      const plugin = plugins.find((p) => p.id === pluginId);
      message.success('\u2705 ' + (plugin?.name || '插件') + ' 安装成功！');
    } catch {
      message.error('安装失败，请稍后重试');
    }
  };

  // 卸载
  const handleUninstall = async (pluginId) => {
    try {
      await uninstall(pluginId);
      setInstalledIds((prev) => {
        const next = new Set(prev);
        next.delete(pluginId);
        return next;
      });
      const plugin = plugins.find((p) => p.id === pluginId);
      message.info((plugin?.name || '插件') + ' 已卸载');
    } catch {
      message.error('操作失败');
    }
  };

  // 分类菜单项
  const menuItems = [
    { key: 'all', icon: categoryIconMap.all, label: '全部' },
    { key: 'featured', icon: categoryIconMap.featured, label: '官方推荐' },
    { key: 'content', icon: categoryIconMap.content, label: '内容创作' },
    { key: 'advertising', icon: categoryIconMap.advertising, label: '广告投放' },
    { key: 'analytics', icon: categoryIconMap.analytics, label: '数据分析' },
    { key: 'influencer', icon: categoryIconMap.influencer, label: '达人营销' },
    { key: 'efficiency', icon: categoryIconMap.efficiency, label: '效率工具' },
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      {/* ====== Banner ====== */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(139,92,246,0.08) 50%, rgba(236,72,153,0.06) 100%)',
          borderRadius: 20,
          border: '1px solid rgba(99,102,241,0.15)',
          padding: '36px 40px',
          marginBottom: 28,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* 背景光效 */}
        <div style={{
          position: 'absolute', top: -60, right: -40, width: 240, height: 240,
          background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <AppstoreOutlined style={{ fontSize: 28, color: '#818cf8' }} />
            <h1 style={{
              margin: 0, fontSize: 28, fontWeight: 700,
              color: 'var(--text-primary)', fontFamily: 'var(--font-sans)',
              letterSpacing: '-0.02em',
            }}>
              Agent 技能市场
            </h1>
            <Badge
              count="NEW"
              style={{
                background: 'linear-gradient(135deg, #E8C85A, #f59e0b)',
                fontSize: 10, fontWeight: 700, padding: '0 6px', height: 18,
                lineHeight: '18px', borderRadius: 6,
              }}
            />
          </div>
          <p style={{
            margin: 0, fontSize: 14, color: 'rgba(240,245,250,0.55)',
            fontFamily: 'var(--font-sans)', lineHeight: 1.6,
          }}>
            扩展你的AI能力边界 —— 安装即用，22款营销技能包助你高效出海
          </p>
          {/* 数据概览 */}
          <div style={{ display: 'flex', gap: 32, marginTop: 20 }}>
            {[
              { icon: <CloudDownloadOutlined />, label: '总插件', value: Object.values(categoryCounts).reduce((a, b) => a + b, 0) || 22 },
              { icon: <CheckCircleOutlined />, label: '已安装', value: installedIds.size },
              { icon: <DollarOutlined />, label: '付费', value: categoryCounts.paid || '10+' },
              { icon: <PieChartOutlined />, label: '免费', value: categoryCounts.free || '12+' },
            ].map((stat) => (
              <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#818cf8', fontSize: 16 }}>{stat.icon}</span>
                <span style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 600 }}>{stat.value}</span>
                <span style={{ color: 'rgba(240,245,250,0.45)', fontSize: 12 }}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ====== 搜索 + Tabs ====== */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, marginBottom: 24, flexWrap: 'wrap',
      }}>
        <Search
          placeholder="搜索插件名称、描述或标签..."
          allowClear
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          style={{ maxWidth: 360, flex: 1 }}
          prefix={<SearchOutlined style={{ color: 'rgba(240,245,250,0.3)' }} />}
          size="large"
        />

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="middle"
          style={{ flexShrink: 0 }}
          items={[
            { key: 'all', label: <span><AppstoreOutlined /> 全部</span> },
            { key: 'installed', label: <span><CheckCircleOutlined /> 已安装 ({installedIds.size})</span> },
            { key: 'paid', label: <span><DollarOutlined /> 付费</span> },
            { key: 'free', label: <span style={{ color: '#52c41a' }}>免费</span> },
          ]}
        />
      </div>

      {/* ====== Body: 分类 + 网格 ====== */}
      <div style={{ display: 'flex', gap: 24, minHeight: 500 }}>
        {/* 左侧分类菜单 */}
        <div style={{ width: 180, flexShrink: 0 }}>
          <Menu
            mode="inline"
            selectedKeys={[activeCategory]}
            onClick={({ key }) => setActiveCategory(key)}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 13,
            }}
            items={menuItems.map((item) => ({
              key: item.key,
              icon: item.icon,
              label: (
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{item.label}</span>
                  <span style={{
                    fontSize: 11, color: 'rgba(240,245,250,0.35)',
                    background: 'rgba(255,255,255,0.05)',
                    padding: '1px 7px', borderRadius: 10,
                  }}>
                    {categoryCounts[item.key] || 0}
                  </span>
                </span>
              ),
            }))}
          />
        </div>

        {/* 右侧插件网格 */}
        <div style={{ flex: 1 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <Spin size="large" />
            </div>
          ) : plugins.length === 0 ? (
            <Empty
              description={
                <span style={{ color: 'rgba(240,245,250,0.4)' }}>
                  没有找到匹配的插件
                </span>
              }
              style={{ padding: '60px 0' }}
            />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory + '-' + activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 16,
                }}
              >
                {plugins.map((plugin) => (
                  <PluginCard
                    key={plugin.id}
                    plugin={plugin}
                    isInstalled={installedIds.has(plugin.id)}
                    onInstall={handleInstall}
                    onUninstall={handleUninstall}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
